import isNil from 'lodash/isNil';
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  datadogLogs,
} from '@datadog/browser-logs';
import {
  useSnapActions,
} from 'views/EMR/stores/snap';
import {
  assessWebmBlob,
} from 'views/EMR/components/Snap/snapDiagnostics';

export type RecordingState = 'idle' | 'recording' | 'paused' | 'ended';

interface UsePopulateRecordReturn {
  recordingState: RecordingState;
  startRecording: () => Promise<void>;
  pauseRecording: () => void;
  resumeRecording: () => void;
  endRecording: () => void;
  recordedBlob: Blob | null;
  duration: number;
  error: string | null;
  isPermissionGranted: boolean;
  clearRecordingStates: () => void;
  stream: MediaStream | null;
}

const errorMessagesByErrorName = {
  NotAllowedError: 'Allow microphone access and try again.',
  PermissionDeniedError: 'Allow microphone access and try again.',
  DevicesNotFoundError: 'No microphone detected.',
  NotReadableError: 'Microphone in use or unavailable.',
  TrackStartError: 'Microphone in use or unavailable.',
  OverconstrainedError: 'Microphone configuration error.',
};

function stopMediaStream(
  streamRef: MutableRefObject<MediaStream | null>,
  shouldClearStream: boolean = false,
): void {
  if (isNil(streamRef.current)) {
    return;
  }

  streamRef.current.getTracks().forEach((track) => {
    track.stop();
  });

  if (shouldClearStream) {
    streamRef.current = null;
  }
}

function usePopulateRecord(): UsePopulateRecordReturn {
  const [recordingState, setRecordingState] = useState<RecordingState>('idle');
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isPermissionGranted, setIsPermissionGranted] = useState(false);

  const { setSnapRecordingState } = useSnapActions();

  useEffect(() => {
    setSnapRecordingState(recordingState);
  }, [recordingState]);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const pausedDurationRef = useRef<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const startRecording = useCallback(async () => {
    try {
      setError(null);

      // Request microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        },
      });

      streamRef.current = stream;
      setStream(stream);
      setIsPermissionGranted(true);

      // Create MediaRecorder instance
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus',
        audioBitsPerSecond: 32000, // or even 24000
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        setRecordingState('ended');
        assessWebmBlob(blob)
          .then((webmAssessment) => {
            try {
              datadogLogs.logger.info('[Snap] Recording finalized', {
                recordingState: 'ended',
                duration: Math.max(0, Math.floor((Date.now() - startTimeRef.current - pausedDurationRef.current) / 1000)),
                blobSize: blob.size,
                blobType: blob.type,
                webmHeaderSignature: webmAssessment.signature,
                webmHeaderValidationStatus: webmAssessment.validationStatus,
              });
            } catch {
              // Best effort diagnostics only.
            }
          })
          .catch(() => {
            // Best effort diagnostics only.
          });

        stopMediaStream(streamRef, true);
      };

      // Start recording
      mediaRecorder.start();
      setRecordingState('recording');
      startTimeRef.current = Date.now();
      pausedDurationRef.current = 0;

      // Start duration timer
      intervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current - pausedDurationRef.current;
        setDuration(Math.floor(elapsed / 1000));
      }, 1000);
    } catch (err: any) {
      console.error('[Snap] Error starting recording', err);
      let msg = 'Failed to start recording';

      if (!isNil(err?.name)) {
        msg = errorMessagesByErrorName[err?.name as keyof typeof errorMessagesByErrorName] ?? 'Failed to start recording';
      }
      setIsPermissionGranted(false);
      setError(msg);
    }
  }, []);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && recordingState === 'recording') {
      mediaRecorderRef.current.pause();
      setRecordingState('paused');
      try {
        datadogLogs.logger.info('[Snap] Recording paused', {
          recordingState: 'paused',
          duration: Math.max(0, Math.floor((Date.now() - startTimeRef.current - pausedDurationRef.current) / 1000)),
        });
      } catch {
        // Best effort diagnostics only.
      }

      // Stop duration timer and record paused time
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [recordingState]);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && recordingState === 'paused') {
      mediaRecorderRef.current.resume();
      setRecordingState('recording');

      // Calculate how much time has passed since recording started
      // and adjust pausedDurationRef so duration calculation stays correct
      const resumeTime = Date.now();
      const totalElapsed = resumeTime - startTimeRef.current;
      // We want: resumeTime - startTimeRef.current - pausedDurationRef.current = duration * 1000
      // So: pausedDurationRef.current = resumeTime - startTimeRef.current - duration * 1000
      pausedDurationRef.current = totalElapsed - duration * 1000;

      datadogLogs.logger.info('[Snap] Recording resumed', {
        recordingState: 'recording',
        duration,
      });

      // Restart duration timer
      intervalRef.current = setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current - pausedDurationRef.current;
        setDuration(Math.floor(elapsed / 1000));
      }, 1000);
    }
  }, [recordingState, duration]);

  const endRecording = useCallback(() => {
    if (mediaRecorderRef.current && (recordingState === 'recording' || recordingState === 'paused')) {
      mediaRecorderRef.current.stop();

      // Stop duration timer
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [recordingState]);

  const clearRecordingStates = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    stopMediaStream(streamRef);
    setRecordingState('idle');
    setRecordedBlob(null);
    setDuration(0);
    setError(null);
    setIsPermissionGranted(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    stopMediaStream(streamRef);
  }, []);

  return {
    recordingState,
    startRecording,
    pauseRecording,
    resumeRecording,
    endRecording,
    recordedBlob,
    duration,
    error,
    isPermissionGranted,
    clearRecordingStates,
    stream,
  };
}

export default usePopulateRecord;
